import { tw } from "@careinfo/tailwind";
import loadable from "@loadable/component";
import { useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { InferType } from "yup";
import CreateCustomer from "../../components/Merchant/CreateCustomerForm";
import Modal from "../../components/Reusable/Modal";
import Spinner from "../../components/Reusable/Spinner";
import APIS from "../../constant/EndPoint";
import useGetHook from "../../customHooks/useGetHook";
import usePatchHook from "../../customHooks/usePatchHook";
import usePostHook from "../../customHooks/usePostHook";
import CreateIcon from "../../Icons/CreateIcon";
import { queryClient } from "../../router";
import { merchantDeliveryChargeTemplateValidationForm } from "../../utils/validationSchemas";

const EditMerchantComponent = loadable(
  () => import("../../components/Merchant/SwitchTab")
);

const EditMerchant = () => {
  const { id } = useParams();
  const [openAddReceiverPopup, setOpenAddReceiverPopup] = useState(false);
  const [openAddSenderPopup, setOpenAddSenderPopup] = useState(false);

  const {
    data: merchant,
    refetch,
    isLoading,
  } = useGetHook({
    queryKey: ["merchant", `merchant-${id}`],
    url: `${APIS.MERCHANT}${id}/`,
    params: {},
  });
  const { data: currentClient, isLoading: isCurrentClientLoading } = useGetHook(
    {
      queryKey: ["curent-client"],
      url: APIS.CLIENT,
      params: {},
    }
  );
  const { data: branchContactData, isLoading: branchContactDataLoading } =
    useGetHook({
      queryKey: [`merchant-${id}-branch-contact-data`],
      url: `${APIS.MERCHANT}${id}/customer-branch-wise-contact/`,
      params: {},
    });

  const { data: cityContactData, isLoading: cityContactDataLoading } =
    useGetHook({
      queryKey: [`merchant-${id}-city-contact-data`],
      url: `${APIS.MERCHANT}${id}/customer-city-wise-contact/`,
      params: {},
    });

  const { mutateAsync: mutateCustomerContactCreateUpdate } = usePostHook({
    queryKey: [
      "customer",
      `merchant-${id}`,
      `merchant-${id}-branch-contact-data`,
      `merchant-${id}-city-contact-data`,
    ],
    navigateURL: "",
  });

  const handleUpdateContact = async (
    contact: string,
    itemId: string,
    branch: boolean,
    city: boolean,
    address: string,
    name: string,
    vat_pan: string,
    setError: any
  ) => {
    const url = `${APIS.CUSTOMER}update-customer-contact/`;
    let formData = {
      contact: contact.startsWith("+977") ? contact : `+977${contact}`,
      address: address,
      name: name,
      vat_pan: vat_pan,
    };
    if (branch && merchant?.default_sender?.id) {
      formData["branch"] = itemId;
      formData["customer"] = merchant?.default_sender?.id;
    } else if (city && merchant?.default_receiver?.id) {
      formData["destination"] = itemId;
      formData["customer"] = merchant?.default_receiver?.id;
    } else {
      toast.error(
        !merchant?.default_sender?.id
          ? "Default sender is required"
          : !merchant?.default_receiver?.id
          ? "Default receiver is required"
          : "Some value is missing"
      );
    }
    try {
      const res = await mutateCustomerContactCreateUpdate({
        url,
        formData,
        setError,
      });
      if (res?.status === 200) {
        queryClient.refetchQueries();
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const { mutateAsync, isLoading: isPatchLoading } = usePatchHook({
    queryKey: ["merchant"],
    navigateURL: "/",
  });

  const handleEdit = async (
    values: InferType<typeof merchantDeliveryChargeTemplateValidationForm>,
    setError: any
  ) => {
    const url = `${APIS.MERCHANT}${id}/`;
    const formData = {
      ...values,
      service_type: values?.service_type
        ? values?.service_type?.map((item) => item?.value)
        : [],
      product_type: values?.product_type
        ? values?.product_type?.map((item) => item?.value)
        : [],
      service_city: values?.service_city
        ? values?.service_city?.map((item) => item?.value)
        : [],
      service_branch: values?.service_branch
        ? values?.service_branch?.map((item) => item?.value)
        : [],
      payment_method: values?.payment_method
        ? values?.payment_method?.map((item) => item?.value)
        : [],
      st_charge: Number(values.st_charge),
    };
    try {
      const res = await mutateAsync({ url, formData, setError });
      if (res?.status === 200) {
        refetch();
      }
    } catch (err) {
      toast.error(err);
    }
  };
  const {
    mutateAsync: createCustomerMutateAsync,
    isPostLoading: createCustomerLoading,
  } = usePostHook({
    queryKey: ["customers"],
    navigateURL: null,
  });

  const handleCreateCustomer = async (values: any, setError: any) => {
    const url = `${APIS.CUSTOMER}`;
    let formData = {
      ...values,
    };
    try {
      const res = await createCustomerMutateAsync({ url, formData, setError });
      if (res.status === 201) {
        setOpenAddReceiverPopup(false);
        setOpenAddSenderPopup(false);
      }
    } catch (err) {
      toast.error(err);
    }
  };
  return (
    <>
      <div
        className={tw`w-full flex justify-left gap-x-2 mb-2 uppercase font-extrabold items-center`}
      >
        <CreateIcon />
        <span className={tw`text-[#5E5757] font-bold text-xl`}>
          Edit Merchant ({merchant?.name}){" "}
        </span>
      </div>
      {isLoading ||
      branchContactDataLoading ||
      cityContactDataLoading ||
      isCurrentClientLoading ? (
        <Spinner />
      ) : (
        <EditMerchantComponent
          isLoading={isPatchLoading}
          handleEdit={handleEdit}
          profile={merchant}
          refetch={refetch}
          currentClient={currentClient}
          openAddReceiverPopup={openAddReceiverPopup}
          setOpenAddReceiverPopup={setOpenAddReceiverPopup}
          openAddSenderPopup={openAddSenderPopup}
          setOpenAddSenderPopup={setOpenAddSenderPopup}
          branchContactData={branchContactData}
          cityContactData={cityContactData}
          handleUpdateContact={handleUpdateContact}
        />
      )}
      <Modal
        isOpen={openAddReceiverPopup}
        setIsOpen={setOpenAddReceiverPopup}
        title={"Craeate Receiver"}
        children={
          <>
            <CreateCustomer
              handleCreate={handleCreateCustomer}
              isLoading={createCustomerLoading}
            />
          </>
        }
      />
      <Modal
        isOpen={openAddSenderPopup}
        setIsOpen={setOpenAddSenderPopup}
        title={"Create Sender"}
        children={
          <>
            <CreateCustomer
              handleCreate={handleCreateCustomer}
              isLoading={createCustomerLoading}
            />
          </>
        }
      />
    </>
  );
};
export default EditMerchant;
